// Copyright (c) Ping. All rights reserved.
//
// Group-join-request envelope (the owner-approval invite flow). When someone
// opens a `/g/<approver>/<groupId>` link, their client starts a 1-1 with the
// approver and sends this request as the first message. The approver's client
// recognises it, folds it out of the chat timeline, and surfaces an Approve
// banner that runs `addWebGroupMembers`.
//
// This rides as a plain JSON envelope decoded app-side (NOT a registered
// protocol kind), so it needs no change to the cross-platform AppEvent registry.
// The `kind` string is stable so desktop/iOS can interoperate (see the parity
// docs).

export const GROUP_JOIN_REQUEST_KIND = 'com.ping.group_join_request';

export interface GroupJoinRequestPayload {
  /** MLS conversation id (hex) of the group the sender wants to join. */
  readonly groupId: string;
  /** Sender's display name, for the approver's UI (best-effort). */
  readonly requesterDisplayName?: string | undefined;
}

/** Encode a join request as envelope bytes for `conv.send(...)`. */
export function encodeGroupJoinRequest(payload: GroupJoinRequestPayload): Uint8Array {
  const out = {
    kind: GROUP_JOIN_REQUEST_KIND,
    payload: {
      groupId: payload.groupId,
      ...(payload.requesterDisplayName !== undefined && payload.requesterDisplayName.length > 0
        ? { requesterDisplayName: payload.requesterDisplayName }
        : {}),
    },
  };
  return new TextEncoder().encode(JSON.stringify(out));
}

export interface DecodedGroupJoinRequest {
  readonly groupId: string;
  readonly requesterDisplayName?: string | undefined;
}

/**
 * Decode a persisted message `body` into a join request, or null if it isn't
 * one. Cheap pre-check on the leading `{` avoids parsing plain-text bodies.
 */
export function decodeGroupJoinRequest(body: string): DecodedGroupJoinRequest | null {
  if (body.length === 0 || body.charAt(0) !== '{') return null;
  try {
    const raw = JSON.parse(body) as { kind?: unknown; payload?: Record<string, unknown> };
    if (raw.kind !== GROUP_JOIN_REQUEST_KIND || typeof raw.payload !== 'object' || raw.payload === null) {
      return null;
    }
    const groupId = raw.payload.groupId;
    if (typeof groupId !== 'string' || groupId.length === 0) return null;
    const name = raw.payload.requesterDisplayName;
    return {
      groupId,
      ...(typeof name === 'string' && name.length > 0 ? { requesterDisplayName: name } : {}),
    };
  } catch {
    return null;
  }
}
